import { authFetch } from '@/auth/token-interceptor';
import { getT, useI18nStore } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useChat } from '@ai-sdk/react';
import { useAISDKRuntime } from '@assistant-ui/ai-sdk';
import { useAui, useRemoteThreadListRuntime } from '@assistant-ui/react';
import { DefaultChatTransport, lastAssistantMessageIsCompleteWithApprovalResponses } from 'ai';
import { f7 } from 'framework7-react';
import { useMemo } from 'react';
import { chatErrorText, chatUrl } from '../api/assistant-api';
import { buildChatRequest } from './chat-request';
import { createThreadListAdapter } from './thread-list-adapter';

function useThreadRuntime() {
  const aui = useAui();
  const restaurantId = useRestaurantStore.getState().selectedRestaurantId ?? '';

  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: chatUrl(restaurantId),
        fetch: authFetch,
        prepareSendMessagesRequest: async ({ messages }) => {
          const { remoteId: threadId } = await aui.threadListItem.initialize();
          return {
            body: buildChatRequest({
              threadId,
              locale: useI18nStore.getState().locale,
              messages,
            }),
          };
        },
      }),
    [aui, restaurantId],
  );

  const chat = useChat({
    transport,
    sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    onError: (error) => {
      f7.toast.show({
        text: chatErrorText(error) ?? getT().aiAssistant.sendFailed,
        closeTimeout: 3000,
        position: 'bottom',
      });
    },
  });

  return useAISDKRuntime(chat);
}

export function useAssistantRuntime(restaurantId: string) {
  const adapter = useMemo(() => createThreadListAdapter(restaurantId), [restaurantId]);
  return useRemoteThreadListRuntime({ adapter, runtimeHook: useThreadRuntime });
}
